<template>
    <div v-show="showSidebar" id="resizable" class="resizable" ref="resizable">
        <div v-if="!isFetch" class="d-flex flex-column h-100">
            <div class="resizable-border" @mousedown="startResize"></div>

            <div class="resizable-button transition-all-0-2"
                 @click="clickResizableButton">
                <i :class="{'rotate': !sidebar.resizableButton}" class="mi-keyboard-arrow-left transition-all-0-2"></i>
            </div>

            <div class="sidebar-block" :class="{'sidebar-block-min': isSmallDesign}">
                <BLink class="button-sidebar mt-2 mb-3"
                       v-if="Object.keys(currentUserAccount).length"
                       :class="{'button-sidebar-min': isSmallDesign, 'active': isActive(['projects'])}"
                       @click="handleClick($event, ['projects'])"
                       href="/projects"
                       v-ripple>
                    <i class="mi-home mi-1-3x"></i>
                    <span :class="{'ml-1': !isSmallDesign}">All projects</span>
                </BLink>
                <SelectProject v-if="Object.keys(currentUserAccount).length"/>
                <LinkContent v-if="projectAvailable" class="mt-3"/>
                <Configuration v-if="showConfiguration" class="mt-3"/>
                <Admin v-if="isSuperAdmin" class="mt-3"/>
            </div>

            <BLink href="https://forms.gle/DgSNZ8zHuhXV3YaKA" target="_blank" class="position-sticky report-issue btn btn-link text-indigo underline-hover p-0 w-100 font-size-sm">
                <span :class="{'font-size-xs': isSmallDesign}">{{isSmallDesign ? 'Report issue' : 'Report an issue or request a feature'}}</span>
            </BLink>
        </div>
        <Timeline v-if="isFetch"/>
    </div>
</template>

<script>
import Configuration from '@/components/sidebar/Configuration';
import Admin from '@/components/sidebar/Admin';
import {FETCH_SIDEBAR} from "@/store/sidebar/actions";
import {mapGetters, mapState} from "vuex";
import '@/styles/sidebar/sidebar.scss';
import SelectProject from "@/components/sidebar/components/SelectProject";
import LinkContent from "@/components/sidebar/components/LinkContent";
import Timeline from "@/components/sidebar/components/Timeline";
import {GetAccountDefaults, GetAccountProperty, UpdateAccountProperty} from "@/utils/local-storage";
import EventBus, {MAIN_WINDOW_CLICK, MAIN_WINDOW_RESIZE, MAIN_WINDOW_SCROLL, SIDEBAR_RESIZE, SIDEBAR_VISIBILITY} from "@/utils/event-bus";
import {SET_SIDEBAR_WIDTH, SET_WINDOW_WIDTH_SIDEBAR} from "@/store/sidebar/mutations";
import sidebarMixin from "@/mixins/sidebar/mixin";
import FuncMixin from '@/mixins/project/content-items/func-mixin';

export default {
    name: 'SidebarBlock',
    components: {
        SelectProject,
        LinkContent,
        Configuration,
        Admin,
        Timeline
    },
    computed: {
        ...mapGetters(['isSmallDesign', 'currentUserCan']),
        ...mapState({
            projectAvailable: state => state.sidebar.projectAvailable,
            isSuperAdmin: state => state.sidebar.isSuperAdmin,
            accountName: state => state.sidebar.accountName,
            isFetch: state => state.sidebar.isFetch,
            currentUserAccount: state => state.sidebar.currentUserAccount,
        }),
        showConfiguration() {
            return this.projectAvailable && (
                this.currentUserCan('manage_workflow')
                || this.currentUserCan('manage_categories')
                || this.currentUserCan('manage_template')
            );
        }
    },
    methods: {
        updateResizableButtonByWidth(width) {
            this.sidebar.resizableButton = width >= this.directionChangeWidth;
        },
        startResize(e) {
            e.preventDefault();
            this.resizing = true;
            this.startX = e.clientX;
            document.addEventListener('mousemove', this.handleMouseMove);
            document.addEventListener('mouseup', this.handleMouseUp);
        },
        handleMouseMove(e) {
            const deltaX = e.clientX - this.startX;
            const newWidth = parseInt(this.$refs.resizable.style.width) + deltaX;
            if (this.resizing && newWidth <= this.maxWidthSidebar && newWidth >= this.minWidthSidebar) {
                this.updateResizableButtonByWidth(newWidth);
                this.sidebar.width = newWidth;
                this.$refs.resizable.style.width = this.sidebar.width + 'px';
                this.startX = e.clientX;
            }
        },
        handleMouseUp() {
            this.resizing = false;
            const newWidth = parseInt(this.$refs.resizable.style.width);
            this.updateResizableButtonByWidth(newWidth);
            document.removeEventListener('mousemove', this.handleMouseMove);
            document.removeEventListener('mouseup', this.handleMouseUp);
            UpdateAccountProperty(this.accountName, 'sidebar', this.sidebar);
        },
        clickResizableButton() {
            this.sidebar.resizableButton = !this.sidebar.resizableButton
            const width = this.sidebar.resizableButton ? this.maxWidthSidebar : this.minWidthSidebar;
            let currentWidth = parseFloat(getComputedStyle(this.$refs.resizable).width);
            const stepsAnimation = 30;
            const step = (width - currentWidth) / stepsAnimation;
            let currentStep = 0;

            let interval = setInterval(() => {
                currentWidth += step;
                currentWidth = Math.round(currentWidth);
                this.sidebar.width = currentWidth;
                this.$refs.resizable.style.width = currentWidth + 'px';

                currentStep++;

                if (currentStep >= stepsAnimation) {
                    clearInterval(interval);
                    UpdateAccountProperty(this.accountName, 'sidebar', this.sidebar);
                }
            }, 10);
        },
        dispatchEventSidebarWidth(width) {
            width = parseInt(width);
            window.dispatchEvent(new CustomEvent(SIDEBAR_RESIZE, {
                detail: {width}
            }));
        },
        stylizeResizableBlock() {

            if (!this.$refs?.resizable || this.isMobile === undefined) {
                return;
            }

            if (this.isMobile) {
                let navbarHeight = this.getNavbarBlock().offsetHeight;
                let top = navbarHeight - this.scrollTopByWindow();
                top = top < 0 ? 0 : top;

                this.$refs.resizable.style.position = 'fixed';
                this.$refs.resizable.style.top = `${top}px`;
                this.stylizedResizable = true;
            } else if (this.stylizedResizable) {
                this.$refs.resizable.style.position = 'sticky';
                this.$refs.resizable.style.top = '0px';
                this.stylizedResizable = false;
            }

        },
        onResize(e) {
            this.$store.commit(SET_WINDOW_WIDTH_SIDEBAR, e.target.innerWidth);
        },
        onShowSidebar(e) {
            this.showSidebar = e?.detail?.visibility ?? false;
        },
        getNavbarBlock() {
            return document.getElementById('navbar-block');
        },
        initObserver() {

            const navbarBlock = this.getNavbarBlock();
            if (!navbarBlock) {
                console.error('Element with id "navbar-block" not found.');
                return;
            }

            this.previousHeight = navbarBlock.offsetHeight;
            this.resizeObserver = new ResizeObserver(entries => {
                for (let entry of entries) {
                    const newHeight = entry.contentRect.height;
                    if (newHeight !== this.previousHeight) {
                        this.stylizeResizableBlock();
                        this.previousHeight = newHeight;
                    }
                }
            });

            this.resizeObserver.observe(navbarBlock);
        }
    },
    mounted() {
        this.initObserver();
        this.$store.commit(SET_WINDOW_WIDTH_SIDEBAR, window.innerWidth);
        this.$store.dispatch(FETCH_SIDEBAR);

        this.sidebar = GetAccountProperty(
            this.accountName,
            'sidebar',
            GetAccountDefaults().sidebar
        );

        if (this.isMobile) {
            let minWidth = 80;
            this.sidebar.width = minWidth;
            this.updateResizableButtonByWidth(minWidth);
        }

        this.$refs.resizable.style.width = this.sidebar.width + 'px';
        const resizableComputedStyle = getComputedStyle(this.$refs.resizable);
        this.maxWidthSidebar = parseInt(resizableComputedStyle.maxWidth);
        this.minWidthSidebar = parseInt(resizableComputedStyle.minWidth);

        window.addEventListener('click', e => EventBus.emit(MAIN_WINDOW_CLICK, e), false);
        window.addEventListener('resize', e => EventBus.emit(MAIN_WINDOW_RESIZE, e), false);
        window.addEventListener('scroll', e => EventBus.emit(MAIN_WINDOW_SCROLL, e), false);
        window.addEventListener(SIDEBAR_VISIBILITY, e => EventBus.emit(SIDEBAR_VISIBILITY, e), false);

        EventBus.on(MAIN_WINDOW_RESIZE, this.onResize);
        EventBus.on(MAIN_WINDOW_SCROLL, this.stylizeResizableBlock);
        EventBus.on(SIDEBAR_VISIBILITY, this.onShowSidebar);
    },
    beforeDestroy() {
        EventBus.off(MAIN_WINDOW_RESIZE, this.onResize);
        EventBus.off(MAIN_WINDOW_SCROLL, this.stylizeResizableBlock);
        EventBus.off(SIDEBAR_VISIBILITY, this.onShowSidebar);

        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
            this.resizeObserver = null;
        }
    },
    watch: {
        ['sidebar.resizableButton']() {
            this.showAfterClick = false;
            setTimeout(() => this.showAfterClick = true, 1000);
        },
        ['sidebar.width']: {
            handler(v) {
                this.$store.commit(SET_SIDEBAR_WIDTH, v);
                this.dispatchEventSidebarWidth(v);
                this.stylizeResizableBlock();
            },
            immediate: true
        },
        isMobile: {
            handler(v) {
                this.showSidebar = !v;
            },
            immediate: true
        }
    },
    mixins: [sidebarMixin, FuncMixin],
    data() {
        return {
            showSidebar: false,
            resizing: false,
            showAfterClick: true,
            startX: 0,
            sidebar: {},
            maxWidthSidebar: 0,
            minWidthSidebar: 0,
            directionChangeWidth: 145,
            stylizedResizable: false,
            resizeObserver: null,
            previousHeight: undefined,
        };
    },
}
</script>
